import { app } from '../../app';
import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import {
  despawnBlockEntity,
  spawnBlockEntity,
  ThickIceBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { BreakEffect } from '../effects/BreakEffect';
import { IceDustEffect } from '../effects/IceDustEffect';
import { CollideHandler, ICollision } from './ICollision';

const sounds = ['puzzle-ice-tap', 'puzzle-door-destroy'];

/*
    thick ice block collision resolver
*/
export class ThickIceCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: ThickIceBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: ThickIceBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    const position = this._entity.c.position.mapPosition;

    app.sound.play(sounds[0], {
      volume: 3,
      pan: mapGetPan(position),
    });

    await new IceDustEffect(this._scene, {
      position,
      amount: 3,
      tint: 0xbbe3ee,
    }).execute();

    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // if block collider
    if (collider?.type === 'block') {
      this._actionAttack();
    }

    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    await this._actionAttack(isOneHitCollider(event.collider?.type));
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _actionAttack(destroyInOneHit = false) {
    const {
      position: { mapPosition },
    } = this._entity.c;

    app.sound.play(sounds[1], { pan: mapGetPan(mapPosition) });

    // destroy the thick ice and replace with a full ice overlay
    despawnBlockEntity(this._scene, this._entity);
    if (destroyInOneHit) {
      // Trigger the goal instead of spawning a new block
      this._scene.sessionSystem.triggerGoal('halfIce', this._entity);
    } else {
      spawnBlockEntity(this._scene, { id: 'fullIce' }, mapPosition);
    }

    return Promise.all([
      new BreakEffect(this._scene, {
        position: mapPosition,
        size: this._entity.c.block.props,
        color: 0xbbe3ee,
        alpha: 0.3,
      }).execute(),
      new IceDustEffect(this._scene, {
        position: mapPosition,
        tint: 0xbbe3ee,
      }).execute(),
    ]);
  }
}
